/* eslint-disable no-unused-expressions */
/* eslint-disable no-use-before-define */
import React, { Fragment, useEffect, useState } from 'react';
import { Route } from 'react-router-dom';

import { useFeatureFlags } from 'Utils';

import { companyUsers } from '../Services/API/company.service';
import RouteSchema from './Routes';

export default function useRoutes(isAuthenticated) {
  const [authorizedRoutes, setAuthorizedRoutes] = useState([]);
  const [unauthorizedRoutes, setUnauthorizedRoutes] = useState([]);
  const [authorizedPaths, setAuthorizedPaths] = useState(['/home']);
  const [unauthorizedPaths, setUnauthorizedPaths] = useState(
    RouteSchema.unauthorized?.flatMap(mapPaths)
  );

  const [companyPermissions, setCompanyPermissions] = useState([]);
  const [currentCompany, setCurrentCompany] = useState({});
  const [permittedRoutes, setPermittedRoutes] = useState([]);
  const [navigation, setNavigation] = useState([]);

  const [role, setRole] = useState('');

  const permissionRouteMap = {
    pipelines: '/pipelines',
    sdi_pipelines: '/sdi_pipelines',
    jobs: '/jobs',
    addon: '/addons',
    manager: '/manager',
    welcome: '/home',
    catalog: '/catalog',
    export: '/export',
    marketplace: '/marketplace',
    chat2dash: '/chat2dash',
    sandbox: '/sandbox',
    visualize: '/visualization'
  };

  const defaultPaths = ['/manager', '/home', '/support'];

  const { hasFeatureAccess } = useFeatureFlags();

  useEffect(() => {
    if (currentCompany?.id) renderCompanyPermissions();
  }, [currentCompany]);

  useEffect(() => {
    if (currentCompany?.id)
      mapRoutePermission(companyPermissions, currentCompany?.id);
  }, [companyPermissions]);

  useEffect(() => {
    if (currentCompany?.id) {
      handleAuthorizedRoutes();
      handleUnauthorizedRoutes();
      handleAuthorizedPaths();
      handleUnauthorizedPaths();
      renderNavigation();
      getCurrentUser();
    }
    // permittedRoutes depends on currentCompany
  }, [permittedRoutes, role]);

  useEffect(() => {
    // if the user is logged-out,
    if (!isAuthenticated) {
      handleUnauthorizedRoutes();
      handleUnauthorizedPaths();
    }
  }, [isAuthenticated]);

  function getCurrentUser() {
    if (currentCompany?.id) {
      const queryParams = `page_size=25&page=1&email=${encodeURIComponent(
        role.email
      )}`;

      if (queryParams.includes('undefined')) {
        console.error('Parâmetros inválidos. A função não será chamada.');
        return;
      }

      companyUsers(currentCompany.id, queryParams)
        .then(({ data }) => {
          const usersData = data.entries;

          if (usersData?.length) {
            setRole(usersData[0].authorizations[0].profile.name);
          }
        })
        .catch((err) => console.log('CompanyUsers Error', err));
    }
  }

  function renderNavigation() {
    const navigationList = RouteSchema.authorized.map((r) => {
      return {
        name: r.name,
        path: r.path,
        siblings: r.siblings || null
      };
    });

    let filteredNavigation = navigationList;
    if (!hasFeatureAccess(role, 'exportAccess', currentCompany?.id)) {
      filteredNavigation = filteredNavigation.filter(
        (el) => el.path !== '/export'
      );
    }

    if (!hasFeatureAccess(role, 'data-catalog', currentCompany?.id)) {
      filteredNavigation = filteredNavigation.filter(
        (el) => el.path !== '/catalog'
      );
    }

    if (!hasFeatureAccess(role, 'marketplace', currentCompany?.id)) {
      filteredNavigation = filteredNavigation.filter(
        (el) => el.path !== '/marketplace'
      );
    }

    if (!hasFeatureAccess(role, 'chat2dash', currentCompany?.id)) {
      filteredNavigation = filteredNavigation.filter(
        (el) => el.path !== '/chat2dash'
      );
    }

    if (!hasFeatureAccess(role, 'sdi_pipelines', currentCompany?.id)) {
      filteredNavigation = filteredNavigation.filter(
        (el) => el.path !== '/sdi_pipelines'
      );
    }

    if (!hasFeatureAccess(role, 'sandbox', currentCompany?.id)) {
      filteredNavigation = filteredNavigation.filter(
        (el) => el.path !== '/sandbox'
      );
    }

    if (!hasFeatureAccess(role, 'pipelines', currentCompany?.id)) {
      filteredNavigation = filteredNavigation.filter(
        (el) => el.path !== '/pipelines'
      );
    }

    if (!hasFeatureAccess(role, 'jobs', currentCompany?.id)) {
      filteredNavigation = filteredNavigation.filter(
        (el) => el.path !== '/jobs'
      );
    }

    if (!hasFeatureAccess(role, 'visualization', currentCompany?.id)) {
      filteredNavigation = filteredNavigation.filter(
        (el) => el.path !== '/visualization'
      );
    }

    console.log(filteredNavigation);

    setNavigation(filteredNavigation);
  }

  function mapPaths(route) {
    const siblings = route.siblings?.map((s) => s?.path);
    const nested = route.nested?.map((n) => n?.path);
    const routePath = route.path;
    if (nested?.length && siblings?.length)
      return [...nested, ...siblings, routePath];
    if (nested?.length) return [...nested, routePath];
    if (siblings?.length) return [...siblings, routePath];
    return routePath;
  }

  function renderCompanyPermissions() {
    const permsRes = JSON.parse(localStorage.getItem('perms'));
    if (permsRes) setCompanyPermissions(permsRes);
  }

  function mapRoutePermission(rawPerms, currCompanyId) {
    if (!hasFeatureAccess(role, 'routingPermissions', currCompanyId)) {
      const finalPermittedRoutes = [
        ...authorizedPaths,
        ...unauthorizedPaths,
        ...defaultPaths
      ];

      setPermittedRoutes(finalPermittedRoutes);
      return finalPermittedRoutes;
    }

    let companyPerms = rawPerms?.find((el) => el.account_id === currCompanyId);

    if (hasFeatureAccess(role, 'exportAccess', currentCompany?.id)) {
      companyPerms = {
        ...companyPerms,
        permissions: [...companyPerms.permissions, 'export']
      };
    }

    if (hasFeatureAccess(role, 'data-catalog', currentCompany?.id)) {
      companyPerms = {
        ...companyPerms,
        permissions: [...companyPerms.permissions, 'catalog']
      };
    }

    if (hasFeatureAccess(role, 'marketplace', currentCompany?.id)) {
      companyPerms = {
        ...companyPerms,
        permissions: [...companyPerms.permissions, 'marketplace']
      };
    }

    if (hasFeatureAccess(role, 'chat2dash', currentCompany?.id)) {
      companyPerms = {
        ...companyPerms,
        permissions: [...companyPerms.permissions, 'chat2dash']
      };
    }

    if (hasFeatureAccess(role, 'sdi_pipelines', currentCompany?.id)) {
      companyPerms = {
        ...companyPerms,
        permissions: [...companyPerms.permissions, 'sdi_pipelines']
      };
    }

    if (hasFeatureAccess(role, 'sandbox', currentCompany?.id)) {
      companyPerms = {
        ...companyPerms,
        permissions: [...companyPerms.permissions, 'sandbox']
      };
    }

    if (hasFeatureAccess(role, 'pipelines', currentCompany?.id)) {
      companyPerms = {
        ...companyPerms,
        permissions: [...companyPerms.permissions, 'pipelines']
      };
    }

    if (hasFeatureAccess(role, 'jobs', currentCompany?.id)) {
      companyPerms = {
        ...companyPerms,
        permissions: [...companyPerms.permissions, 'jobs']
      };
    }

    if (hasFeatureAccess(role, 'visualization', currentCompany?.id)) {
      companyPerms = {
        ...companyPerms,
        permissions: [...companyPerms.permissions, 'visualization']
      };
    }

    const mappedPermittedRoutes = companyPerms?.permissions?.map(
      (el) => permissionRouteMap?.[el]
    );

    let orderedRoutes = [];

    mappedPermittedRoutes &&
      Object.values(mappedPermittedRoutes).forEach((el) => {
        if (mappedPermittedRoutes.includes(el)) {
          orderedRoutes = [el, ...orderedRoutes];
        }
      });

    const finalPermittedRoutes = [
      ...orderedRoutes,
      ...unauthorizedPaths,
      ...defaultPaths
    ];

    setPermittedRoutes(finalPermittedRoutes);

    return finalPermittedRoutes;
  }

  function mapRoutes(route) {
    if (route.siblings?.length) {
      return route.siblings?.map(mapRoutes);
    }

    return (
      // TODO: check perms for nested routes
      hasFeatureAccess(role, 'routingPermissions', currentCompany?.id) ? (
        permittedRoutes?.filter((el) => route?.path.includes(el)).length ? (
          <Fragment key={route?.name}>
            <Route
              path={route?.path}
              render={route?.render}
              exact={route?.exact}
              key={route?.name}
            />
            {route.nested?.map(mapRoutes)}
          </Fragment>
        ) : (
          []
        )
      ) : (
        <Fragment key={route?.name}>
          <Route
            path={route?.path}
            render={route?.render}
            exact={route?.exact}
            key={route?.name}
          />
          {route.nested?.map(mapRoutes)}
        </Fragment>
      )
    );
  }

  function handleAuthorizedRoutes() {
    const authRoutes = RouteSchema.authorized?.flatMap(mapRoutes);
    setAuthorizedRoutes(authRoutes);
  }

  function handleUnauthorizedRoutes() {
    const unauthRoutes = RouteSchema.unauthorized?.flatMap(mapRoutes);
    setUnauthorizedRoutes(unauthRoutes);
  }

  function handleAuthorizedPaths() {
    const authPaths = RouteSchema.authorized
      .map((el) => {
        const mapped = mapPaths(el);
        return mapped;
      })
      .flatMap((el) => {
        return hasFeatureAccess(role, 'routingPermissions', currentCompany?.id)
          ? permittedRoutes.flatMap((perm) => {
              return el.includes(perm) ? el : [];
            })
          : el;
      });
    setAuthorizedPaths(authPaths);
  }

  function handleUnauthorizedPaths() {
    const unauthPaths = RouteSchema.unauthorized?.flatMap(mapPaths);
    setUnauthorizedPaths(unauthPaths);
  }

  return {
    navigation,
    setNavigation,
    renderNavigation,
    permissionRouteMap,
    authorizedPaths,
    authorizedRoutes,
    unauthorizedPaths,
    unauthorizedRoutes,
    handleAuthorizedPaths,
    permittedRoutes,
    mapRoutePermission,
    setCurrentCompany,
    setRole
  };
}
